import { ChannelList, useChatContext } from "stream-chat-react";
import { useState, useCallback, useEffect } from "react";

import styled from "styled-components";
import {
  EmptyDMChannelListIndicator,
  EmptyGroupChannelListIndicator,
} from "./EmptyChannelListIndicator";
import ChannelSearch from "../ChannelSearch/ChannelSearch";
import TeamChannelList from "../TeamChannelList/TeamChannelList";
import ChannelPreview from "../ChannelPreview/ChannelPreview";

import "features/messages/src/styles/Sidebar/index.scss";

const SidebarContainer = styled.div`
  position: relative;
  width: ${(props) => props.$width}px;
  min-width: 200px;
  max-width: 800px;
  height: 100%;
  background: #f5f5f5;
  transition: width 0.1s ease;
  overflow: hidden;

  @media only screen and (max-width: 600px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: 100%;
    transform: translateX(${(props) => (props.$isMobileOpen ? "0" : "-100%")});
    transition: transform 0.3s ease;
    z-index: 50;
  }
`;

const SidebarContent = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;

  .str-chat__channel-list {
    --str-chat__channel-list-border-inline-end: none;
  }

  .sidebar {
    display: flex;
    height: 100%;

    .channel-list-bar {
      display: flex;
      flex-direction: column;
      width: 100% !important;
      flex: 1;
      min-height: 0;

      .channel-list-bar__header {
        padding-inline: 16px;
        height: var(--header-height);
      }

      .channel-list-bar__scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

        &::-webkit-scrollbar {
          width: 8px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.35);
        }
      }

      .channel-preview {
        display: flex;
        align-items: center;
        width: 100% !important;
        height: 37px;
        padding: 0;

        &.selected {
          display: flex;
          align-items: center;
          height: 37px;
        }

        .channel-preview__item {
          display: flex;
          align-items: center;
          max-width: none !important;
          width: 100% !important;
          height: 100%;
          padding-left: 26px;
          padding-right: 16px;

          p {
            flex: 1;
            min-width: 0;
            text-align: start;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          > .ant-avatar {
            margin-right: 12px;
            flex-shrink: 0;
          }
        }

        .team-channel-list--group .channel-preview__item p {
          width: auto !important;
          flex: 1;
        }

        .team-channel-list--dm .channel-preview__item p {
          width: auto !important;
          flex: 1;
        }
      }
    }
  }
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  &:active {
    background: rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 20px;
    background: #ccc;
    border-radius: 1px;
  }

  &:hover::after {
    background: #999;
  }

  @media only screen and (max-width: 600px) {
    display: none;
  }
`;

const options = { state: true, watch: true, presence: true, limit: 30 };
const sort = { last_message_at: -1, updated_at: -1 };

const customChannelTeamFilter = (channels) => {
  const filtered = channels.filter((channel) => channel.type === "team");
  return filtered;
};

const customChannelMessagingFilter = (channels) => {
  const filtered = channels.filter((channel) => channel.type === "messaging");
  return filtered;
};

function TeamListComponent(listProps) {
  return <TeamChannelList {...listProps} type="team" />;
}

function TeamPreviewComponent(previewProps) {
  return <ChannelPreview {...previewProps} type="team" />;
}

function MessagingListComponent(listProps) {
  return <TeamChannelList {...listProps} type="messaging" />;
}

function MessagingPreviewComponent(previewProps) {
  return <ChannelPreview {...previewProps} type="messaging" />;
}

function TeamChannelsList() {
  const { client } = useChatContext();
  const teamFilters = { type: "team", members: { $in: [client.userID] } };

  return (
    <ChannelList
      channelRenderFilterFn={customChannelTeamFilter}
      filters={teamFilters}
      options={options}
      sort={sort}
      EmptyStateIndicator={EmptyGroupChannelListIndicator}
      List={TeamListComponent}
      Preview={TeamPreviewComponent}
    />
  );
}

function MessagingChannelsList() {
  const { client } = useChatContext();

  const messagingFilters = {
    type: "messaging",
    members: { $in: [client.userID] },
  };

  return (
    <ChannelList
      channelRenderFilterFn={customChannelMessagingFilter}
      filters={messagingFilters}
      options={options}
      sort={sort}
      EmptyStateIndicator={EmptyDMChannelListIndicator}
      List={MessagingListComponent}
      Preview={MessagingPreviewComponent}
    />
  );
}

export function Sidebar({ isMobileOpen = false }) {
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 800) {
        setSidebarWidth(newWidth);
      }
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add event listeners for mouse move and mouse up
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <SidebarContainer $width={sidebarWidth} $isMobileOpen={isMobileOpen}>
      <SidebarContent>
        <div className="sidebar">
          <div className="channel-list-bar">
            <div className="channel-list-bar__header">
              <p className="channel-list-bar__header__text">Messages</p>
            </div>
            <ChannelSearch />
            <div className="channel-list-bar__scroll">
              <TeamChannelsList />
              <MessagingChannelsList />
            </div>
          </div>
        </div>
      </SidebarContent>
      <ResizeHandle onMouseDown={handleMouseDown} />
    </SidebarContainer>
  );
}
